import { Box, Text, useToast } from "@chakra-ui/react";
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from "react-hook-form";
import { useUserInfo } from "../../../utils/hooks/useUserInfo";
import SwitchToggle from "../../common/Switch";
import { SearchAndFilterForm } from "./utils";
import { Input } from "../../common";
import { useEffect, useState } from "react";
import { IncludeExcludeRadio } from "../../common/IncludeExcludeRadio";

type ContactEngagementProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  prefix?: string;
};

function ContactEngagement({
  control,
  setValue,
  prefix = "",
}: ContactEngagementProps) {
  const toast = useToast();
  const [dialedInError, setDialedInError] = useState<string | null>(null);
  const eventFields = [
    {
      label: "No Outbound Activity",
      value: `${prefix}filter_and_sort.contact_engagement.no_outbound_activity`,
    },
    {
      label: "No Inbound Activity",
      value: `${prefix}filter_and_sort.contact_engagement.no_inbound_activity`,
    },
    {
      label: "Has Outbound Messages",
      value: `${prefix}filter_and_sort.contact_engagement.has_outbound_messages`,
    }, {
      label: "Has Outbound Emails",
      value: `${prefix}filter_and_sort.contact_engagement.has_outbound_emails`,
    },
    {
      label: "Has Inbound Activity",
      value: `${prefix}filter_and_sort.contact_engagement.has_inbound_activity`,
    },
    {
      label: "Has Unread Messages",
      value: `${prefix}filter_and_sort.contact_engagement.has_unread_messages`,
    },
    {
      label: "Unassigned Leads",
      value: `${prefix}filter_and_sort.contact_engagement.unassigned_leads`,
    },
    {
      label: "New Leads",
      value: `${prefix}filter_and_sort.contact_engagement.new_leads`,
    },
    {
      label: "Enrolled in Campaigns ",
      value: `${prefix}filter_and_sort.contact_engagement.enrolled_in_campaigns`,
    },
  ];

  const dialedIn = useWatch({
    control,
    name: `${prefix}filter_and_sort.contact_engagement.dialed_in`,
  });

  const xDays = useWatch({
    control,
    name: `${prefix}filter_and_sort.contact_engagement.x_days`,
  });
  
  const xHours = useWatch({
    control,
    name: `${prefix}filter_and_sort.contact_engagement.x_hours`,
  });

  const exclusive = useWatch({
    control,
    name: `${prefix}filter_and_sort.contact_engagement.exclusive`,
  });

  useEffect(() => {
    if (dialedIn && !xDays && !xHours) {
      setDialedInError("Please enter either X Days or X Hours");
    } else {
      setDialedInError(null);
    }
  }, [dialedIn, xDays, xHours]);

  return (
    <>
      <Box background="var(--secondary-50)" padding="1.25rem">
        <Text textStyle="h4" color="var(--grey-900)" fontWeight="600">
          Contact Engagement
        </Text>
      </Box>
      <IncludeExcludeRadio
        display="flex"
        justifyContent="center"
        mt="0.5rem"
        value={exclusive}
        onChange={() => {
          setValue(
            `${prefix}filter_and_sort.contact_engagement.exclusive`,
            !exclusive
          );
        }}
      />
      <Controller
        render={({ field: { onChange, value }, fieldState: { error } }) => {
          return (
            <Box mb="1rem" mt="1rem" paddingLeft="1.25rem">
              <SwitchToggle
                label="If dialed in the last X days or hours, will be removed"
                id="#last-response_contact_engagement"
                defaultChecked={value}
                onChange={(checked: boolean) => {
                  setValue(
                    `${prefix}filter_and_sort.contact_engagement.dialed_in`,
                    checked
                  );

                    if (!checked) {
                      setValue(
                        `${prefix}filter_and_sort.contact_engagement.x_hours`,
                        ""
                      );
                      setValue(
                        `${prefix}filter_and_sort.contact_engagement.x_days`,
                        ""
                      );
                      setDialedInError(null);
                    } else {
                      setDialedInError("Please enter either X Days or X Hours");
                    }
                }}
              />
            </Box>
          );
        }}
        name={`${prefix}filter_and_sort.contact_engagement.dialed_in`}
        control={control}
        rules={{
          required: true,
        }}
      />

      {dialedInError && (
        <Text color="red.500" fontSize="sm" paddingLeft="1.25rem">
          {dialedInError}
        </Text>
      )}

      {dialedIn && (
        <Box padding="1.25rem" display="grid" gridTemplateColumns="1fr 1fr">
          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Box mb="1rem" mr="1rem">
                  <Input
                    label=""
                    type="number"
                    placeholder="X Days"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                    w="100%"
                  />
                </Box>
              );
            }}
            name={`${prefix}filter_and_sort.contact_engagement.x_days`}
            control={control}
            rules={{
              required: true,
            }}
          />

          <Controller
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <Box mb="1rem">
                  <Input
                    label=""
                    type="number"
                    placeholder="X Hours"
                    value={value}
                    onChange={onChange}
                    error={error?.message}
                    w="100%"
                  />
                </Box>
              );
            }}
            name={`${prefix}filter_and_sort.contact_engagement.x_hours`}
            control={control}
            rules={{
              required: true,
            }}
          />
        </Box>
      )}

      <Box padding="1.25rem" display="grid" gridTemplateColumns="1fr 1fr">
        {eventFields.map((eventField) => {
          return (
            <Controller
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => {
                return (
                  <Box mb="1.5rem">
                    <SwitchToggle
                      label={eventField.label}
                      id={`#${eventField.label.split(" ").join("-")}`}
                      defaultChecked={value}
                      onChange={(checked: boolean) => {
                        setValue(eventField.value as any, checked)
                      }}
                    />
                  </Box>
                );
              }}
              name={eventField.value as any}
              control={control}
              rules={{
                required: true,
              }}
            />
          );
        })}
      </Box>
    </>
  );
}

export default ContactEngagement;
